<template>
	<div>
		<Breadcrumbs main="App" title="Draw Results" />
		<div class="container-fluid">

			<!-- List card -->
			<div class="row">
				<div class="col-12">
					<div class="card">
						<div class="card-header d-flex justify-content-between align-items-center">
							<div>
								<h5>Draw Result Management</h5>
								<span>Review draw outcomes and update winning numbers.</span>
							</div>
							<div class="d-flex gap-2">
								<button
									v-if="canAddResult"
									class="btn btn-primary"
									type="button"
									@click="openCreateModal"
								>
									Add Draw Result
								</button>
								<button class="btn btn-outline-primary" type="button" @click="fetchResults" :disabled="loading">
									Refresh
								</button>
							</div>
						</div>

						<div class="card-body">
							<div v-if="errorMessage" class="alert alert-danger" role="alert">{{ errorMessage }}</div>
							<div v-if="successMessage" class="alert alert-success" role="alert">{{ successMessage }}</div>

							<!-- Date filters -->
							<div class="row g-3 mb-3 align-items-end">
								<div class="col-md-4">
									<label class="form-label">Start Date</label>
									<input v-model="filter.startDate" type="date" class="form-control" @change="onFilterChange" />
								</div>
								<div class="col-md-4">
									<label class="form-label">End Date</label>
									<input v-model="filter.endDate" type="date" class="form-control" @change="onFilterChange" />
								</div>
								<div class="col-md-4">
									<label class="form-label">Search</label>
									<input v-model="searchTerm" type="text" class="form-control" placeholder="Filter by draw name or prize" />
								</div>
							</div>

							<div class="table-responsive">
								<table class="table table-striped table-bordered">
									<thead>
										<tr>
											<th>Draw Name</th>
											<th>Draw Time</th>
											<th>1st Prize</th>
											<th>2nd Prize</th>
											<th>3rd Prize</th>
											<th>Recorded At</th>
											<th class="text-center">Actions</th>
										</tr>
									</thead>
									<tbody>
										<tr v-if="loading">
											<td colspan="7" class="text-center">Loading...</td>
										</tr>
										<tr v-else-if="!filteredResults.length">
											<td colspan="7" class="text-center">No draw results found.</td>
										</tr>
										<tr v-for="result in filteredResults" :key="result.drawResultId">
											<td>{{ result.drawName || '-' }}</td>
											<td>{{ formatDate(result.drawTime) }}</td>
											<td>{{ result.firstPrize || '-' }}</td>
											<td>{{ result.secondPrize || '-' }}</td>
											<td>{{ result.thirdPrize || '-' }}</td>
											<td>{{ formatDate(result.resultCreatedAt) }}</td>
											<td class="text-center">
												<button
													v-if="canUpdate"
													class="btn btn-sm btn-warning"
													type="button"
													@click="openEdit(result)"
												>
													Edit
												</button>
											</td>
										</tr>
									</tbody>
								</table>
							</div>

							<!-- Pagination -->
							<div v-if="computedTotalPages > 1" class="d-flex justify-content-between align-items-center mt-2">
								<small class="text-muted">
									Page {{ pagination.pageNumber }} of {{ computedTotalPages }} ({{ computedTotalItems }} records)
								</small>
								<div class="d-flex gap-2">
									<button
										class="btn btn-sm btn-outline-secondary"
										:disabled="pagination.pageNumber <= 1"
										@click="goToPage(pagination.pageNumber - 1)"
									>
										&laquo; Prev
									</button>
									<button
										class="btn btn-sm btn-outline-secondary"
										:disabled="pagination.pageNumber >= computedTotalPages"
										@click="goToPage(pagination.pageNumber + 1)"
									>
										Next &raquo;
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Edit form -->
			<div class="row" v-if="showForm && canUpdate">
				<div class="col-12">
					<div class="card">
						<div class="card-header d-flex justify-content-between align-items-center">
							<h5 class="mb-0">Update Draw Result — {{ form.drawName }} [{{ formatDate(form.drawTime) }}]</h5>
							<button class="btn btn-outline-secondary btn-sm" type="button" @click="closeForm">Close</button>
						</div>
						<div class="card-body">
							<form @submit.prevent="saveResult">
								<div class="row g-3">
									<!-- Read-only meta fields -->
									<div class="col-md-4">
										<label class="form-label">Draw Name</label>
										<input :value="form.drawName" type="text" class="form-control" readonly />
									</div>
									<div class="col-md-4">
										<label class="form-label">Draw Time</label>
										<input :value="formatDate(form.drawTime)" type="text" class="form-control" readonly />
									</div>
									<div class="col-md-4">
										<label class="form-label">Recorded At</label>
										<input :value="formatDate(form.resultCreatedAt)" type="text" class="form-control" readonly />
									</div>

									<!-- Primary prizes -->
									<div class="col-12 mt-2"><h6>Primary Prizes</h6></div>
									<div class="col-md-4">
										<label class="form-label">First Prize</label>
										<input
											v-model.trim="form.firstPrize"
											type="text"
											:class="['form-control', { 'duplicate-field': isDuplicateValue(editDuplicateGroups.primary, form.firstPrize) }]"
										/>
									</div>
									<div class="col-md-4">
										<label class="form-label">Second Prize</label>
										<input
											v-model.trim="form.secondPrize"
											type="text"
											:class="['form-control', { 'duplicate-field': isDuplicateValue(editDuplicateGroups.primary, form.secondPrize) }]"
										/>
									</div>
									<div class="col-md-4">
										<label class="form-label">Third Prize</label>
										<input
											v-model.trim="form.thirdPrize"
											type="text"
											:class="['form-control', { 'duplicate-field': isDuplicateValue(editDuplicateGroups.primary, form.thirdPrize) }]"
										/>
									</div>

									<!-- Starter prizes -->
									<div class="col-12 mt-2"><h6>Starter Prizes</h6></div>
									<div class="col-md-3" v-for="(_, idx) in form.starterPrizes" :key="'sp' + idx">
										<label class="form-label">Starter {{ idx + 1 }}</label>
										<input
											:value="form.starterPrizes[idx]"
											@input="updatePrize('starterPrizes', idx, $event.target.value)"
											type="text"
											:class="['form-control', { 'duplicate-field': isDuplicateValue(editDuplicateGroups.starter, form.starterPrizes[idx]) }]"
										/>
									</div>

									<!-- Consolation prizes -->
									<div class="col-12 mt-2"><h6>Consolation Prizes</h6></div>
									<div class="col-md-3" v-for="(_, idx) in form.consolationPrizes" :key="'cp' + idx">
										<label class="form-label">Consolation {{ idx + 1 }}</label>
										<input
											:value="form.consolationPrizes[idx]"
											@input="updatePrize('consolationPrizes', idx, $event.target.value)"
											type="text"
											:class="['form-control', { 'duplicate-field': isDuplicateValue(editDuplicateGroups.consolation, form.consolationPrizes[idx]) }]"
										/>
									</div>

									<div class="col-12 d-flex gap-2 mt-3">
										<button class="btn btn-primary" type="submit" :disabled="submitting">
											{{ submitting ? 'Saving...' : 'Update Result' }}
										</button>
										<button class="btn btn-outline-secondary" type="button" @click="closeForm">Cancel</button>
									</div>
								</div>
							</form>
						</div>
					</div>
				</div>
			</div>

			<!-- Create modal -->
			<div v-if="showCreateModal && canAddResult" class="modal-backdrop-custom" @click="closeCreateModal">
				<div class="modal-dialog-custom" @click.stop>
					<div class="card mb-0">
						<div class="card-header d-flex justify-content-between align-items-center">
							<h5 class="mb-0">Add Draw Result</h5>
							<button class="btn btn-outline-secondary btn-sm" type="button" @click="closeCreateModal">Close</button>
						</div>
						<div class="card-body">
							<form @submit.prevent="saveNewResult">
								<div class="row g-3">
									<div class="col-12">
										<label class="form-label">Closed Draw</label>
										<select v-model="createForm.drawId" class="form-select" :disabled="loadingClosedDraws" required>
											<option disabled value="">Select a closed draw</option>
											<option v-for="draw in closedDraws" :key="draw.id" :value="draw.id">
												{{ draw.drawName }} [{{ formatDate(draw.drawTime) }}]
											</option>
										</select>
										<small v-if="loadingClosedDraws" class="text-muted">Loading available draws...</small>
									</div>

									<div class="col-12 mt-2"><h6>Primary Prizes (4 digits)</h6></div>
									<div class="col-md-4">
										<label class="form-label">First Prize</label>
										<input
											:value="createForm.firstPrize"
											@input="createForm.firstPrize = normalizePrizeInput($event.target.value)"
											type="text"
											inputmode="numeric"
											maxlength="4"
											:class="['form-control', { 'duplicate-field': isDuplicateValue(createDuplicateGroups.primary, createForm.firstPrize) }]"
											required
										/>
									</div>
									<div class="col-md-4">
										<label class="form-label">Second Prize</label>
										<input
											:value="createForm.secondPrize"
											@input="createForm.secondPrize = normalizePrizeInput($event.target.value)"
											type="text"
											inputmode="numeric"
											maxlength="4"
											:class="['form-control', { 'duplicate-field': isDuplicateValue(createDuplicateGroups.primary, createForm.secondPrize) }]"
											required
										/>
									</div>
									<div class="col-md-4">
										<label class="form-label">Third Prize</label>
										<input
											:value="createForm.thirdPrize"
											@input="createForm.thirdPrize = normalizePrizeInput($event.target.value)"
											type="text"
											inputmode="numeric"
											maxlength="4"
											:class="['form-control', { 'duplicate-field': isDuplicateValue(createDuplicateGroups.primary, createForm.thirdPrize) }]"
											required
										/>
									</div>

									<div class="col-12 mt-2"><h6>Starter Prizes (4 digits)</h6></div>
									<div class="col-md-3" v-for="(_, idx) in createForm.starterPrizes" :key="'new-sp' + idx">
										<label class="form-label">Starter {{ idx + 1 }}</label>
										<input
											:value="createForm.starterPrizes[idx]"
											@input="updateCreatePrize('starterPrizes', idx, $event.target.value)"
											type="text"
											inputmode="numeric"
											maxlength="4"
											:class="['form-control', { 'duplicate-field': isDuplicateValue(createDuplicateGroups.starter, createForm.starterPrizes[idx]) }]"
											required
										/>
									</div>

									<div class="col-12 mt-2"><h6>Consolation Prizes (4 digits)</h6></div>
									<div class="col-md-3" v-for="(_, idx) in createForm.consolationPrizes" :key="'new-cp' + idx">
										<label class="form-label">Consolation {{ idx + 1 }}</label>
										<input
											:value="createForm.consolationPrizes[idx]"
											@input="updateCreatePrize('consolationPrizes', idx, $event.target.value)"
											type="text"
											inputmode="numeric"
											maxlength="4"
											:class="['form-control', { 'duplicate-field': isDuplicateValue(createDuplicateGroups.consolation, createForm.consolationPrizes[idx]) }]"
											required
										/>
									</div>

									<div class="col-12 d-flex gap-2 mt-3">
										<button class="btn btn-primary" type="submit" :disabled="creating || loadingClosedDraws || !createForm.drawId">
											{{ creating ? 'Saving...' : 'Save Draw Result' }}
										</button>
										<button class="btn btn-outline-secondary" type="button" @click="closeCreateModal">Cancel</button>
									</div>
								</div>
							</form>
						</div>
					</div>
				</div>
			</div>

		</div>
	</div>
</template>

<script>
import { API_BASE as BASE_URL } from '../../config/api';
const LIST_ENDPOINT = `${BASE_URL}/api/DrawResult`;
const UPDATE_ENDPOINT = `${BASE_URL}/api/DrawResult`;
const INCOMPLETE_DRAW_ENDPOINT = `${BASE_URL}/api/Draw/incomplete`;
const CONDUCT_DRAW_ENDPOINT = `${BASE_URL}/api/Draw`;

function createEmptyForm() {
	return {
		drawResultId: '',
		drawId: '',
		drawName: '',
		drawTime: '',
		firstPrize: '',
		secondPrize: '',
		thirdPrize: '',
		starterPrizes: Array(10).fill(''),
		consolationPrizes: Array(10).fill(''),
		resultCreatedAt: '',
	};
}

function createEmptyCreateForm() {
	return {
		drawId: '',
		firstPrize: '',
		secondPrize: '',
		thirdPrize: '',
		starterPrizes: Array(10).fill(''),
		consolationPrizes: Array(10).fill(''),
	};
}

export default {
	name: 'DrawResultIndex',
	data() {
		return {
			results: [],
			closedDraws: [],
			loading: false,
			submitting: false,
			creating: false,
			loadingClosedDraws: false,
			showForm: false,
			showCreateModal: false,
			searchTerm: '',
			errorMessage: '',
			successMessage: '',
			form: createEmptyForm(),
			createForm: createEmptyCreateForm(),
			filter: {
				startDate: '',
				endDate: '',
			},
			pagination: {
				pageNumber: 1,
				pageSize: 20,
				totalItems: 0,
				totalPages: 1,
			},
		};
	},
	computed: {
		permissions() {
			return this.$store?.state?.auth?.permissions || [];
		},
		canRead() {
			return this.hasAnyPermission(['drawresult:read']);
		},
		canUpdate() {
			return this.hasAnyPermission(['drawresult:update']);
		},
		canAddResult() {
			return this.hasAnyPermission(['drawresult:create']);
		},
		editDuplicateGroups() {
			return this.getDuplicateGroups(this.form);
		},
		createDuplicateGroups() {
			return this.getDuplicateGroups(this.createForm);
		},
		filteredSortedResults() {
			const sortedResults = [...this.results].sort((a, b) => {
				const aTime = new Date(a.drawTime || 0).getTime();
				const bTime = new Date(b.drawTime || 0).getTime();
				return bTime - aTime;
			});

			const startDate = this.filter.startDate ? new Date(`${this.filter.startDate}T00:00:00`) : null;
			const endDate = this.filter.endDate ? new Date(`${this.filter.endDate}T23:59:59`) : null;
			const dateFiltered = sortedResults.filter((result) => {
				const drawDate = result.drawTime ? new Date(result.drawTime) : null;
				if (!drawDate || isNaN(drawDate.getTime())) return false;
				if (startDate && drawDate < startDate) return false;
				if (endDate && drawDate > endDate) return false;
				return true;
			});

			const query = this.searchTerm.trim().toLowerCase();
			if (!query) return dateFiltered;
			return dateFiltered.filter((result) =>
				[result.drawName, result.firstPrize, result.secondPrize, result.thirdPrize, result.drawId]
					.some(v => String(v || '').toLowerCase().includes(query))
			);
		},
		filteredResults() {
			const start = (this.pagination.pageNumber - 1) * this.pagination.pageSize;
			const end = start + this.pagination.pageSize;
			return this.filteredSortedResults.slice(start, end);
		},
		computedTotalItems() {
			return this.filteredSortedResults.length;
		},
		computedTotalPages() {
			if (this.computedTotalItems === 0) return 1;
			return Math.ceil(this.computedTotalItems / this.pagination.pageSize);
		},
	},
	watch: {
		searchTerm() {
			this.pagination.pageNumber = 1;
		},
		'filter.startDate'() {
			this.pagination.pageNumber = 1;
		},
		'filter.endDate'() {
			this.pagination.pageNumber = 1;
		},
	},
	mounted() {
		this.$store.dispatch('auth/loadPermissions');
		if (this.canRead) {
			this.fetchResults();
		} else {
			this.errorMessage = 'You do not have draw result read permission.';
		}
	},
	methods: {
		hasAnyPermission(list) {
			return list.some(p => this.permissions.includes(p));
		},
		authHeaders(includeJson = false) {
			const token = localStorage.getItem('token');
			const headers = {};
			if (includeJson) headers['Content-Type'] = 'application/json';
			if (token) headers.Authorization = `Bearer ${token}`;
			return headers;
		},
		clearMessages() {
			this.errorMessage = '';
			this.successMessage = '';
		},
		getDuplicateSet(values) {
			const counts = {};
			values
				.map(v => String(v || '').trim())
				.filter(v => v.length > 0)
				.forEach((v) => {
					counts[v] = (counts[v] || 0) + 1;
				});

			return new Set(Object.keys(counts).filter(v => counts[v] >= 2));
		},
		getDuplicateGroups(formData) {
			if (!formData) {
				return {
					primary: new Set(),
					starter: new Set(),
					consolation: new Set(),
				};
			}

			return {
				primary: this.getDuplicateSet([formData.firstPrize, formData.secondPrize, formData.thirdPrize]),
				starter: this.getDuplicateSet(formData.starterPrizes || []),
				consolation: this.getDuplicateSet(formData.consolationPrizes || []),
			};
		},
		isDuplicateValue(duplicateSet, value) {
			const normalizedValue = String(value || '').trim();
			return normalizedValue.length > 0 && duplicateSet.has(normalizedValue);
		},
		validateNoDuplicates(formData) {
			const groups = this.getDuplicateGroups(formData);

			if (groups.primary.size > 0) {
				return 'Duplicate numbers are not allowed in First/Second/Third prize group.';
			}
			if (groups.starter.size > 0) {
				return 'Duplicate numbers are not allowed in Starter prizes group.';
			}
			if (groups.consolation.size > 0) {
				return 'Duplicate numbers are not allowed in Consolation prizes group.';
			}

			return '';
		},
		validateResultForm(formData, requireDrawId = false) {
			if (requireDrawId && !formData.drawId) {
				return 'Please select a closed draw.';
			}

			const values = [
				formData.firstPrize,
				formData.secondPrize,
				formData.thirdPrize,
				...(formData.starterPrizes || []),
				...(formData.consolationPrizes || []),
			];

			if (values.some(v => !/^\d{4}$/.test(String(v || '').trim()))) {
				return 'All 23 prize fields must be exactly 4 digits.';
			}

			return this.validateNoDuplicates(formData);
		},
		formatDate(value) {
			if (!value) return '-';
			const d = new Date(value);
			if (isNaN(d.getTime())) return '-';
			const dd = String(d.getDate()).padStart(2, '0');
			const mm = String(d.getMonth() + 1).padStart(2, '0');
			const yyyy = d.getFullYear();
			return `${dd}/${mm}/${yyyy}`;
		},
		normalizeResult(result) {
			return {
				drawResultId: result.drawResultId || '',
				drawId: result.drawId || '',
				drawName: result.drawName || '',
				drawTime: result.drawTime || '',
				firstPrize: result.firstPrize || '',
				secondPrize: result.secondPrize || '',
				thirdPrize: result.thirdPrize || '',
				starterPrizes: Array.isArray(result.starterPrizes)
					? result.starterPrizes.map(p => p || '')
					: Array(10).fill(''),
				consolationPrizes: Array.isArray(result.consolationPrizes)
					? result.consolationPrizes.map(p => p || '')
					: Array(10).fill(''),
				resultCreatedAt: result.resultCreatedAt || '',
			};
		},
		onFilterChange() {
			this.pagination.pageNumber = 1;
		},
		async fetchResults() {
			this.clearMessages();
			this.loading = true;
			try {
				const response = await fetch(`${LIST_ENDPOINT}`, { headers: this.authHeaders(false) });
				if (!response.ok) throw new Error(`HTTP ${response.status}`);
				const data = await response.json();

				const items = Array.isArray(data) ? data : (Array.isArray(data.items) ? data.items : []);
				this.results = items.map(this.normalizeResult);
				this.pagination.pageNumber = 1;
				this.pagination.totalItems = this.results.length;
				this.pagination.totalPages = Math.max(1, Math.ceil(this.results.length / this.pagination.pageSize));
			} catch (error) {
				this.errorMessage = `Failed to load draw results: ${error.message}`;
			} finally {
				this.loading = false;
			}
		},
		goToPage(page) {
			if (page < 1 || page > this.computedTotalPages) return;
			this.pagination.pageNumber = page;
		},
		openEdit(result) {
			this.clearMessages();
			this.form = this.normalizeResult(result);
			this.showForm = true;
		},
		closeForm() {
			this.showForm = false;
			this.form = createEmptyForm();
		},
		normalizePrizeInput(value) {
			return String(value || '').replace(/\D/g, '').slice(0, 4);
		},
		updateCreatePrize(field, idx, value) {
			this.createForm[field].splice(idx, 1, this.normalizePrizeInput(value));
		},
		async fetchClosedDraws() {
			this.loadingClosedDraws = true;
			try {
				const response = await fetch(INCOMPLETE_DRAW_ENDPOINT, { headers: this.authHeaders(false) });
				if (!response.ok) throw new Error(`Failed to load draws (HTTP ${response.status})`);
				const data = await response.json();
				const draws = Array.isArray(data) ? data : (Array.isArray(data.items) ? data.items : []);
				this.closedDraws = draws.map((d) => ({
					id: d.drawId,
					drawName: d.drawName,
					drawTime: d.drawTime,
				}));
			} finally {
				this.loadingClosedDraws = false;
			}
		},
		async openCreateModal() {
			this.clearMessages();
			try {
				await this.fetchClosedDraws();
				if (!this.closedDraws.length) {
					this.errorMessage = 'No closed draws available for adding results.';
					return;
				}
				this.createForm = createEmptyCreateForm();
				this.showCreateModal = true;
			} catch (error) {
				this.errorMessage = error.message || 'Failed to open add result form.';
			}
		},
		closeCreateModal() {
			this.showCreateModal = false;
			this.createForm = createEmptyCreateForm();
		},
		buildCreatePayload() {
			const payload = {
				drawId: this.createForm.drawId,
				firstPrize: this.createForm.firstPrize,
				secondPrize: this.createForm.secondPrize,
				thirdPrize: this.createForm.thirdPrize,
			};

			this.createForm.starterPrizes.forEach((v, i) => {
				payload[`starterPrize${i + 1}`] = v;
			});
			this.createForm.consolationPrizes.forEach((v, i) => {
				payload[`consolationPrize${i + 1}`] = v;
			});

			return payload;
		},
		validateCreateForm() {
			return this.validateResultForm(this.createForm, true);
		},
		async saveNewResult() {
			this.clearMessages();
			const validationError = this.validateCreateForm();
			if (validationError) {
				this.errorMessage = validationError;
				return;
			}

			this.creating = true;
			try {
				const response = await fetch(`${CONDUCT_DRAW_ENDPOINT}/${encodeURIComponent(this.createForm.drawId)}/conduct`, {
					method: 'POST',
					headers: this.authHeaders(true),
					body: JSON.stringify(this.buildCreatePayload()),
				});

				if (!response.ok) {
					let message = `HTTP ${response.status}`;
					try {
						const err = await response.json();
						message = err.message || err.title || JSON.stringify(err);
					} catch {
						// ignore non-JSON error body
					}
					throw new Error(message);
				}

				await this.fetchResults();
				this.closeCreateModal();
				this.successMessage = 'Draw result added successfully.';
			} catch (error) {
				this.errorMessage = `Failed to add draw result: ${error.message}`;
			} finally {
				this.creating = false;
			}
		},
		updatePrize(field, idx, value) {
			this.form[field].splice(idx, 1, value.trim());
		},
		buildPayload() {
			const payload = {
				firstPrize: this.form.firstPrize || null,
				secondPrize: this.form.secondPrize || null,
				thirdPrize: this.form.thirdPrize || null,
				starterPrizes: this.form.starterPrizes.map(p => p || null),
				consolationPrizes: this.form.consolationPrizes.map(p => p || null),
			};
			// Include individual fields for API compatibility
			this.form.starterPrizes.forEach((v, i) => {
				payload[`starterPrize${i + 1}`] = v || null;
			});
			this.form.consolationPrizes.forEach((v, i) => {
				payload[`consolationPrize${i + 1}`] = v || null;
			});
			return payload;
		},
		async saveResult() {
			if (!this.form.drawResultId) {
				this.errorMessage = 'Result id is required for update.';
				return;
			}

			const validationError = this.validateResultForm(this.form, false);
			if (validationError) {
				this.errorMessage = validationError;
				return;
			}
			this.clearMessages();
			this.submitting = true;
			try {
				const response = await fetch(`${UPDATE_ENDPOINT}/${encodeURIComponent(this.form.drawResultId)}`, {
					method: 'PUT',
					headers: this.authHeaders(true),
					body: JSON.stringify(this.buildPayload()),
				});
				if (!response.ok) {
					let message = `HTTP ${response.status}`;
					try {
						const err = await response.json();
						message = err.message || err.title || JSON.stringify(err);
					} catch {
						// ignore non-JSON error body
					}
					throw new Error(message);
				}
				await this.fetchResults();
				this.closeForm();
				this.successMessage = 'Draw result updated successfully.';
			} catch (error) {
				this.errorMessage = `Failed to update draw result: ${error.message}`;
			} finally {
				this.submitting = false;
			}
		},
	},
};
</script>

<style scoped>
.modal-backdrop-custom {
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.45);
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 1rem;
	z-index: 1050;
}

.modal-dialog-custom {
	width: min(1100px, 100%);
	max-height: 90vh;
	overflow-y: auto;
}

.duplicate-field {
	background-color: #ffe8e8;
	border-color: #f2a6a6;
}
</style>
